import { Maximize2Icon, Minimize2Icon } from "lucide-react";

import { RightSidebarShell } from "../../components/RightSidebarShell";
import {
  RightPanelInlineSidebar,
  RightPanelSheet,
} from "../../components/ThreadRightPanelRouteLayout";
import { Button } from "../../components/ui/button";
import { useIsMobile, useMediaQuery } from "../../hooks/useMediaQuery";
import { RIGHT_PANEL_FULLSCREEN_MEDIA_QUERY } from "../../components/rightPanelLayout";
import type { PullRequestRightPanelSurface, RightPanelSurface } from "../../rightPanelSurface";
import { PullRequestPanel } from "./PullRequestsPage.views";

interface PullRequestsPanelLayoutProps {
  readonly open: boolean;
  readonly maximized: boolean;
  readonly surfaces: ReadonlyArray<RightPanelSurface>;
  readonly activeSurface: PullRequestRightPanelSurface | null;
  readonly activeSurfaceId: string | null;
  readonly onSelectSurface: (surfaceId: string) => void;
  readonly onCloseSurface: (surfaceId: string) => void;
  readonly onToggleMaximized: () => void;
  readonly onClose: () => void;
}

export function PullRequestsPanelLayout(props: PullRequestsPanelLayoutProps) {
  const isMobile = useIsMobile();
  const useFullscreenMobileSheet = useMediaQuery(RIGHT_PANEL_FULLSCREEN_MEDIA_QUERY);
  const panel = pullRequestPanelShell(props);
  return isMobile ? (
    <RightPanelSheet
      open={panel !== null}
      fullscreen={props.maximized || useFullscreenMobileSheet}
      onClose={props.onClose}
    >
      {panel}
    </RightPanelSheet>
  ) : (
    <RightPanelInlineSidebar
      open={panel !== null}
      maximized={props.maximized}
      defaultWidth={typeof window === "undefined" ? 640 : Math.floor(window.innerWidth / 2)}
      storageKey="forge:pull-request-panel-width"
      onClose={props.onClose}
    >
      {panel}
    </RightPanelInlineSidebar>
  );
}

function pullRequestPanelShell(props: PullRequestsPanelLayoutProps) {
  if (!props.open || props.activeSurface === null) return null;
  return (
    <RightSidebarShell
      surfaces={props.surfaces}
      activeSurfaceId={props.activeSurfaceId}
      isServerThread={false}
      onSelectSurface={props.onSelectSurface}
      onCloseSurface={props.onCloseSurface}
      onAddSurface={() => undefined}
      showAddSurface={false}
      onClose={props.onClose}
      layoutControls={
        <Button
          size="icon"
          variant="ghost"
          className="size-11 rounded-lg text-muted-foreground sm:size-6 sm:rounded-md"
          aria-label={panelLayoutControlLabel(props.maximized)}
          onClick={props.onToggleMaximized}
        >
          <PanelLayoutControlIcon maximized={props.maximized} />
        </Button>
      }
    >
      <PullRequestPanel surface={props.activeSurface} />
    </RightSidebarShell>
  );
}

function panelLayoutControlLabel(maximized: boolean): string {
  return maximized ? "Restore pull request panel" : "Maximize pull request panel";
}

function PanelLayoutControlIcon({ maximized }: { readonly maximized: boolean }) {
  if (maximized) return <Minimize2Icon aria-hidden="true" className="size-4 sm:size-3.5" />;
  return <Maximize2Icon aria-hidden="true" className="size-4 sm:size-3.5" />;
}
